import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import cookie from "js-cookie";
import { setToken } from "./login";
import { toast } from "components/ui/use-toast";
import last from "lodash/last";
import isNil from "lodash/isNil";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
  }).format(amount);
};

export const getIsoDate = (date: Date) =>
  `${date.getFullYear()}-${String((date.getMonth() || 0) + 1).padStart(
    2,
    "0"
  )}-${String(date.getDate()).padStart(2, "0")}`;

export function addHours(date: Date, hours: number) {
  date.setHours(date.getHours() + hours);
  return date;
}

export const getMonday = (date: Date) => {
  const d = new Date(date);

  const day = d.getDay();
  const diff = d.getDate() - day + (day == 0 ? -6 : 1); // adjust when day is sunday

  return new Date(new Date(d.setDate(diff)).setHours(0, 0, 0, 0));
};

export const getSunday = (date: Date) => {
  const d = new Date(date);

  const day = d.getDay();
  const diff = d.getDate() + 7 - (day == 0 ? 7 : day); // adjust when day is sunday

  return new Date(new Date(d.setDate(diff)).setHours(0, 0, 0, 0));
};

export const refreshToken = async () => {
  const refreshToken = localStorage.getItem("refresh_token");
  const apiKey = process.env.NEXT_PUBLIC_FIREBASE_API_KEY;

  if (!refreshToken) return;

  const response = await fetch(
    process.env.NEXT_PUBLIC_ENV
      ? `https://securetoken.googleapis.com/v1/token?key=${apiKey}`
      : `http://localhost:9099/securetoken.googleapis.com/v1/token?key=${apiKey}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json", // Adjust the content type as needed
        // Add any other headers if required
      },
      body: JSON.stringify({
        grant_type: "refresh_token",
        refresh_token: refreshToken,
      }),
    }
  );

  const data = await response.json();

  setToken(data.access_token);
};

async function _performAuthenticatedCall<T>(
  route: string,
  query: string,
  method: "GET" | "POST" | "DELETE" | "PUT" | "PATCH",
  body?: string
) {
  const params: RequestInit = {
    method,
    headers: {
      "Content-Type": "application/json", // Adjust the content type as needed
      // Add any other headers if required
      Authorization: `Bearer ${cookie.get("access_token")}`,
    },
  };

  if (method !== "GET" && body) {
    params["body"] = body;
  }

  const response = await fetch(
    `${process.env.NEXT_PUBLIC_API_ORIGIN}${route}${query ? `?${query}` : ""}`,
    params
  );

  const result = await response.json();

  if (result.statusCode && !String(result.statusCode).startsWith("2")) {
    throw result;
  }

  return result as T;
}

export async function performAuthenticatedCall<T>(
  route: string,
  query: string,
  method: "GET" | "POST" | "DELETE" | "PUT" | "PATCH",
  body?: string
) {
  try {
    const data = await _performAuthenticatedCall<T>(route, query, method, body);

    return data;
  } catch (error) {
    if (error.statusCode === 401) {
      await refreshToken();

      return _performAuthenticatedCall<T>(route, query, method, body);
    }

    if (error.statusCode && !String(error.statusCode).startsWith("2")) {
      const description =
        error.statusCode === 403
          ? `${error.message} ${route}`
          : Array.isArray(error.message)
          ? error.message.join(", ")
          : error.message;

      toast({
        duration: (description.length / 10) * 1000,
        variant: "destructive",
        title: description,
      });

      throw error;
    }

    throw error;
  }
}

async function _performAuthenticatedUpload<T>(
  route: string,
  fieldName: string,
  file: File,
  fileName?: string
) {
  const formData = new FormData();

  formData.append(fieldName, file, fileName);

  const params: RequestInit = {
    method: "POST",
    headers: {
      Authorization: `Bearer ${cookie.get("access_token")}`,
    },
  };

  params["body"] = formData;

  const response = await fetch(
    `${process.env.NEXT_PUBLIC_API_ORIGIN}${route}`,
    params
  );

  const result = await response.json();

  if (result.statusCode && !String(result.statusCode).startsWith("2")) {
    throw result;
  }

  return result as T;
}

export async function performAuthenticatedUpload<T>(
  route: string,
  fieldName: string,
  file: File,
  fileName?: string
) {
  try {
    const data = await _performAuthenticatedUpload<T>(
      route,
      fieldName,
      file,
      fileName
    );

    return data;
  } catch (error) {
    if (error.statusCode === 401) {
      await refreshToken();

      return _performAuthenticatedUpload<T>(route, fieldName, file, fileName);
    }

    if (error.statusCode && !String(error.statusCode).startsWith("2")) {
      const description =
        error.statusCode === 403
          ? `${error.message} ${route}`
          : Array.isArray(error.message)
          ? error.join(", ")
          : error.message;

      toast({
        duration: (description.length / 10) * 1000,
        variant: "destructive",
        title: description,
      });

      throw error;
    }

    throw error;
  }
}

export const getUnitLongFrenchName = (
  unit: "none" | "grams" | "milliliters"
) => {
  if (unit === "none") return "unité";
  if (unit === "grams") return "grammes";
  if (unit === "milliliters") return "millilitres";

  return "";
};

export const getUnitShortFrenchName = (
  unit: "none" | "grams" | "milliliters"
) => {
  if (unit === "none") return "";
  if (unit === "grams") return "gr.";
  if (unit === "milliliters") return "ml.";

  return "";
};

export const getDisplayableQuantity = (
  quantity: number,
  unit: "none" | "grams" | "milliliters",
  mode: "short" | "long"
) => {
  if (unit === "milliliters" && quantity > 999) {
    return `${Intl.NumberFormat("fr-FR", {
      style: "decimal",
      maximumFractionDigits: 2,
      minimumFractionDigits: 0,
    }).format(quantity / 1000)}${mode === "short" ? "l." : "litre(s)"}`;
  }

  if (unit === "milliliters" && quantity <= 999) {
    return `${Intl.NumberFormat("fr-FR", {
      style: "decimal",
      maximumFractionDigits: 2,
      minimumFractionDigits: 0,
    }).format(quantity)} ${mode === "short" ? "ml." : "millilitres"}`;
  }

  if (unit === "grams" && quantity > 999) {
    return `${Intl.NumberFormat("fr-FR", {
      style: "decimal",
      maximumFractionDigits: 2,
      minimumFractionDigits: 0,
    }).format(quantity / 1000)} ${mode === "short" ? "kg." : "kilogrammes"}`;
  }

  if (unit === "grams" && quantity <= 999) {
    return `${Intl.NumberFormat("fr-FR", {
      style: "decimal",
      maximumFractionDigits: 2,
      minimumFractionDigits: 0,
    }).format(quantity)} ${mode === "short" ? "gr." : "grammes"}`;
  }

  return Intl.NumberFormat("fr-FR", {
    style: "decimal",
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
  }).format(quantity);
};

export const getMonthRanges = (date: Date) => {
  const dayRange = [date, date];
  const sunday = getSunday(date);

  const weekRange = [
    getMonday(date),
    new Date(sunday.setDate(sunday.getDate())),
  ];

  const lastDayOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0);

  const monthRange = [
    new Date(date.getFullYear(), date.getMonth(), 1),
    new Date(lastDayOfMonth.setDate(lastDayOfMonth.getDate())),
  ];

  return { dayRange, weekRange, monthRange };
};

export const getYearRanges = (date: Date) => {
  const currentMonth = date.getMonth();

  const currentQuarter = Math.floor(currentMonth / 3);

  const quarterBounds: Record<string, [number, number]> = {
    "0": [0, 3],
    "1": [3, 6],
    "2": [6, 9],
    "3": [9, 12],
  };

  const currentSemester = Math.floor(currentMonth / 6);

  const semesterBounds: Record<string, [number, number]> = {
    "0": [0, 6],
    "1": [6, 12],
  };

  const quarterRange = [
    new Date(date.getFullYear(), quarterBounds[`${currentQuarter}`][0], 1),
    new Date(date.getFullYear(), quarterBounds[`${currentQuarter}`][1], 0),
  ];
  const semesterRange = [
    new Date(date.getFullYear(), semesterBounds[`${currentSemester}`][0], 1),
    new Date(date.getFullYear(), semesterBounds[`${currentSemester}`][1], 0),
  ];
  const yearRange = [
    new Date(date.getFullYear(), 0, 1),
    new Date(date.getFullYear(), 11, 31),
  ];

  return { quarterRange, semesterRange, yearRange };
};

export type Ranges =
  | "today"
  | "week"
  | "month"
  | "quarter"
  | "semester"
  | "year";
export type DateRange = Array<Date>;

export const getFromAndTo = (
  monthRanges: {
    dayRange: DateRange;
    weekRange: DateRange;
    monthRange: DateRange;
  } | null,
  yearRanges: {
    quarterRange: DateRange;
    semesterRange: DateRange;
    yearRange: DateRange;
  } | null,
  range: Ranges,
  date: Date
) => {
  if (monthRanges !== null) {
    return range === "today"
      ? monthRanges.dayRange
      : range === "week"
      ? monthRanges.weekRange
      : monthRanges.monthRange;
  }

  if (yearRanges === null) return [date, date];

  return range === "quarter"
    ? yearRanges.quarterRange
    : range === "semester"
    ? yearRanges.semesterRange
    : yearRanges.yearRange;
};

export const isToday = (date: Date) => {
  const today = new Date();

  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
};

export const getStoreIdFromSlug = (slug: string | Array<string>) => {
  if (Array.isArray(slug)) return {};

  return last(slug.split("-"));
};

export function base64ToBlob(base64String: string, contentType = "") {
  const byteCharacters = atob(base64String.split(",")[1]);
  const byteArrays = [];

  for (let offset = 0; offset < byteCharacters.length; offset += 512) {
    const slice = byteCharacters.slice(offset, offset + 512);
    const byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }

  return new Blob(byteArrays, { type: contentType });
}

export function isMobile() {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );
}

const getNumberOfDaysBetweenTwoDates = (from: Date, to: Date) => {
  // One day in milliseconds
  const oneDay = 1000 * 60 * 60 * 24;

  // Calculate the time difference between two dates
  const diffInTime = to.getTime() - from.getTime();

  // Calculate the number of days between two dates
  const diffInDays = Math.round(diffInTime / oneDay);

  return diffInDays;
};

export function getNumberOfDays(start: string, end: string) {
  const date1 = new Date(start);
  const date2 = new Date(end);

  return getNumberOfDaysBetweenTwoDates(date1, date2);
}

function subtractDays(date: Date, days: number) {
  const result = new Date(date);
  result.setDate(result.getDate() - days);
  return result;
}

function isLastDayOfMonth(date: Date) {
  // Create a new date object for the next day
  const nextDay = new Date(date);
  nextDay.setDate(date.getDate() + 1);

  // Check if the next day is the first day of the next month
  return nextDay.getDate() === 1;
}

export const getPreviousPeriod = (from: Date, to: Date) => {
  if (from.getTime() > to.getTime()) {
    throw new Error("from > to: interval is invalid");
  }

  const fromMonth = from.getMonth();
  const fromDay = from.getDate();
  const toMonth = to.getMonth();
  const toDate = to.getDate();
  const fromYear = from.getFullYear();
  const toYear = to.getFullYear();

  // year
  if (
    fromMonth === 0 &&
    fromDay === 1 &&
    toMonth === 11 &&
    toDate === 31 &&
    fromYear === toYear
  ) {
    return {
      previousFrom: `${fromYear - 1}-01-01`,
      previousTo: `${toYear - 1}-12-31`,
    };
  }

  // semester
  // s1
  if (fromMonth === 0 && fromDay === 1 && toMonth === 5 && toDate === 30) {
    return {
      previousFrom: `${fromYear - 1}-07-01`,
      previousTo: `${toYear - 1}-12-31`,
    };
  }

  // s2
  if (fromMonth === 6 && fromDay === 1 && toMonth === 11 && toDate === 31) {
    return {
      previousFrom: `${fromYear}-01-01`,
      previousTo: `${toYear}-06-30`,
    };
  }

  // quarter
  // q1
  if (fromMonth === 0 && fromDay === 1 && toMonth === 2 && toDate === 31) {
    return {
      previousFrom: `${fromYear - 1}-10-01`,
      previousTo: `${toYear - 1}-12-31`,
    };
  }

  // q2
  if (fromMonth === 3 && fromDay === 1 && toMonth === 5 && toDate === 30) {
    return {
      previousFrom: `${fromYear}-01-01`,
      previousTo: `${toYear}-03-31`,
    };
  }

  // q3
  if (fromMonth === 6 && fromDay === 1 && toMonth === 8 && toDate === 30) {
    return {
      previousFrom: `${fromYear}-04-01`,
      previousTo: `${toYear}-06-30`,
    };
  }

  // q4
  if (fromMonth === 9 && fromDay === 1 && toMonth === 11 && toDate === 31) {
    return {
      previousFrom: `${fromYear}-07-01`,
      previousTo: `${toYear}-09-30`,
    };
  }

  // month
  // m1
  if (fromMonth === 0 && fromDay === 1 && toMonth === 0 && toDate === 31) {
    return {
      previousFrom: `${fromYear - 1}-12-01`,
      previousTo: `${toYear - 1}-12-31`,
    };
  }

  // m[2-12]
  if (fromDay === 1 && isLastDayOfMonth(to) && fromMonth === toMonth) {
    return {
      previousFrom: `${fromYear}-${fromMonth.toString().padStart(2, "0")}-01`,
      previousTo: `${toYear}-${toMonth
        .toString()
        .padStart(2, "0")}-${subtractDays(from, 1).getDate()}`,
    };
  }

  // other
  const numberOfDays = getNumberOfDaysBetweenTwoDates(from, to);
  const previousFrom = subtractDays(from, numberOfDays);
  const previousTo = subtractDays(to, numberOfDays);

  return {
    previousFrom: getIsoDate(previousFrom),
    previousTo: getIsoDate(previousTo),
  };
};

export const getRelativeDate = (date: Date) => {
  const today = new Date();

  const dateFormatter = new Intl.DateTimeFormat("fr-FR", {
    dateStyle: "short",
  });

  const timeFormatter = new Intl.DateTimeFormat("fr-FR", {
    timeStyle: "short",
  });

  const time = timeFormatter.format(new Date(date));

  const formattedDate = `${dateFormatter.format(new Date(date))} à ${time}`;

  const diffDays = today.getDate() - date.getDate();

  // same everything event day of the month
  if (
    diffDays === 0 &&
    today.getMonth() === date.getMonth() &&
    today.getFullYear() === date.getFullYear()
  ) {
    return `Aujourd'hui à ${time}`;
  }

  // one day difference on the same year of the same year
  const datePlus1Day = addHours(date, 24);
  if (
    diffDays === 1 &&
    today.getMonth() === datePlus1Day.getMonth() &&
    today.getMonth() === datePlus1Day.getMonth() &&
    today.getFullYear() === datePlus1Day.getFullYear()
  ) {
    return `Hier à ${time}`;
  }

  const todayPlus1Day = addHours(today, 24);

  // tomorrow same month same year
  if (
    diffDays === -1 &&
    todayPlus1Day.getMonth() === date.getMonth() &&
    todayPlus1Day.getMonth() === date.getMonth() &&
    todayPlus1Day.getFullYear() === date.getFullYear()
  ) {
    return `Demain à ${time}`;
  }

  // else

  return formattedDate;
};

export function getRelativeTime(date: Date) {
  const rtf = new Intl.RelativeTimeFormat('fr-FR', { numeric: 'auto' });
  const now = new Date();
  const diff = date.getTime() - now.getTime();

  const units: Record<
    'year' | 'month' | 'day' | 'hour' | 'minute' | 'second',
    number
  > = {
    year: 24 * 60 * 60 * 1000 * 365,
    month: (24 * 60 * 60 * 1000 * 365) / 12,
    day: 24 * 60 * 60 * 1000,
    hour: 60 * 60 * 1000,
    minute: 60 * 1000,
    second: 1000,
  };

  const entries = Object.entries(units);

  for (const [unit, ms] of entries) {
    if (Math.abs(diff) >= ms || unit === 'second') {
      return rtf.format(
        Math.round(diff / ms),
        unit as 'year' | 'month' | 'day' | 'hour' | 'minute' | 'second',
      );
    }
  }
}

export const getQueryString = (object: Record<string, unknown>): string => {
  const entries = Object.keys(object).filter((e) => !isNil(object[e]));

  if (!entries.length) return "";

  return entries.reduce(
    (acc, current, index) => 
      `${acc}${index === 0 ? '' : '&'}${encodeURIComponent(current)}=${encodeURIComponent(String(object[current]))}`,
    ""
  );
};